import moment from 'moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Field } from './Field'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { User } from './User'

type LocationConstructorArgs = ConstructorArgs<() => Location> & { name_display: string | null }

export class Location extends BaseRecord {
    readonly recordType = 'location'

    name?: string
    royalty?: number
    marketing?: number
    royaltyTax?: boolean
    royaltyRefund?: boolean
    royaltyGratuity?: boolean
    royaltyItems?: string[]
    cancelledDate?: moment.Moment
    nextRoyaltyDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    fieldValues?: RecordDataMap
    private readonly nameDisplay?: string

    constructor(data?: Partial<LocationConstructorArgs>) {
        super(data)
        if (!data) {
            this.royaltyItems = []
            this.fieldValues = new RecordDataMap()
            return
        }

        this.name = data.name ?? undefined
        this.royalty = this.propertyFromNumberArg(data.royalty)
        this.marketing = this.propertyFromNumberArg(data.marketing)
        this.royaltyTax = data.royalty_tax ?? false
        this.royaltyRefund = data.royalty_refund ?? false
        this.royaltyGratuity = data.royalty_gratuity ?? false
        this.royaltyItems = data.royalty_items ?? undefined
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.fieldValues = this.propertyFromDictionaryArg(data.field_values)
        this.nameDisplay = data.name_display ?? undefined
    }

    clone(overrides?: Partial<LocationConstructorArgs>): Location {
        return new Location({ ...this.serializeForClone(), ...overrides })
    }

    get nameDisplayString(): string {
        if (this.name?.length) return this.name
        return this.nameDisplay || ''
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.instance) this.instance = BaseRecord.fromSerializer(data.instance, User)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.nextRoyaltyDate) this.nextRoyaltyDate = moment(data.nextRoyaltyDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.fieldValues) this.fieldValues = new RecordDataMap(data.fieldValues)
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name ?? ''
        dict['royalty'] = this.royalty ?? ''
        dict['marketing'] = this.marketing ?? ''
        dict['royalty_tax'] = this.royaltyTax
        dict['royalty_refund'] = this.royaltyRefund
        dict['royalty_gratuity'] = this.royaltyGratuity
        dict['royalty_items'] = this.royaltyItems ?? ''
        if (this.fieldValues)
            dict['field_values'] = Field.serializeFieldData(
                this.fieldValues,
                this.owner?.id,
                undefined
            )
        return dict
    }

    static fk: FKType = {
        users: ['owner', 'instance'],
        sources: ['source'],
    }

    static dataType(): DataType {
        return 'locations'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get instance(): User | undefined {
        return this.getForeignRecord('instance', User)
    }
    set instance(instance: User | undefined) {
        this.setForeignRecord('instance', instance)
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
